import { useEffect } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import { format } from "date-fns";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { StyledTableCell, StyledTableRow } from "../../../styles/tableStyles";
import { RoleTypes } from "../../auth/types/roleTypes";
import { isPrivilegedTransaction } from "../../../utils/authUtils";
import { selectAuth } from "../../auth/authSlice";
import { fetchComplaints, selectComplaints } from "../complaintSlice";
import { ComplaintStatusChip } from "./ComplaintStatusChip";

type EmployeeComplaintsPanelProps = {
  employeeId: string;
};

const COMPLAINT_ROLES = [
  RoleTypes.OpenComplains,
  RoleTypes.EditComplains,
  RoleTypes.ClosComplains,
  RoleTypes.ReOpenComplains,
  RoleTypes.ConfirmComplains,
];

export const EmployeeComplaintsPanel = ({
  employeeId,
}: EmployeeComplaintsPanelProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { user } = useAppSelector(selectAuth);
  const roles = user?.roles ?? [];
  const {
    complaintsWithCount: { complaints },
    loading,
  } = useAppSelector(selectComplaints);

  const canView = COMPLAINT_ROLES.some((r) =>
    isPrivilegedTransaction(roles, r),
  );

  useEffect(() => {
    if (!canView || !employeeId) return;
    dispatch(
      fetchComplaints({
        skip: 0,
        take: 20,
        employeeId,
      }),
    );
  }, [dispatch, employeeId, canView]);

  if (!canView) return null;

  return (
    <Accordion defaultExpanded={complaints.length > 0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1">{t("complaints")}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {loading === "pending" ? (
          <Typography variant="body2" color="text.secondary">
            {t("loading")}...
          </Typography>
        ) : complaints.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("noComplaintsFound")}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>{t("subject")}</StyledTableCell>
                  <StyledTableCell>{t("type")}</StyledTableCell>
                  <StyledTableCell>{t("status")}</StyledTableCell>
                  <StyledTableCell>{t("openedOn")}</StyledTableCell>
                  <StyledTableCell>{t("actions")}</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {complaints.map((row) => (
                  <StyledTableRow key={row._id}>
                    <StyledTableCell>{row.subject}</StyledTableCell>
                    <StyledTableCell>{te(row.type)}</StyledTableCell>
                    <StyledTableCell>
                      <ComplaintStatusChip status={row.status} />
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.createdAt
                        ? format(new Date(row.createdAt), "dd-MMM-yyyy")
                        : "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/app/complaints/${row._id}`}
                      >
                        {t("view")}
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
